import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Package, History, RefreshCw, Edit, AlertTriangle, CheckCircle, Clock, Save, X, Trash, Plus } from 'lucide-react';
import { useCachedManualCheck } from '@/hooks/useApi';
import { useRegisterRefresh } from '@/contexts/RefreshContext';
import { useDataCache } from '@/contexts/DataCacheContext';
import { simulatorsApi, ordersApi, getApiBaseUrl } from '@/services/api';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import Modal from '@/components/common/Modal';
import { extractAuditChanges, formatAuditChanges, formatAuditAction, formatAuditTimestamp } from '@/utils/auditUtils';

type SystemType = 'gkpos' | 'sap_ecc' | 'hybris';

interface SystemConfig {
  name: string;
  displayName: string;
  color: string;
  description: string;
}

const SystemPage: React.FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  
  const API_BASE = getApiBaseUrl();

  // Map URL params to system types
  const systemMapping: Record<string, SystemType> = {
    'gkpos': 'gkpos',
    'sap-ecc': 'sap_ecc', 
    'hybris': 'hybris'
  };

  const systemType = systemId ? systemMapping[systemId] : 'gkpos';
  
  const systemConfigs: Record<SystemType, SystemConfig> = {
    gkpos: {
      name: 'gkpos',
      displayName: 'GKPOS',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      description: 'Point of Sale and Order Management System'
    },
    sap_ecc: {
      name: 'sap_ecc',
      displayName: 'SAP ECC',
      color: 'bg-green-100 text-green-800 border-green-200',
      description: 'Enterprise Resource Planning System'
    },
    hybris: {
      name: 'hybris',
      displayName: 'Hybris',
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      description: 'E-commerce and Customer Experience Platform'
    }
  };

  const currentSystem = systemConfigs[systemType];
  const { clearCache } = useDataCache();

  // DIRECT API APPROACH - Bypass the custom hook entirely
  const [directOrders, setDirectOrders] = useState<any[] | null>(null);
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState<string | null>(null);

  // Direct audit trail loading state
  const [auditData, setAuditData] = useState<{
    auditTrail: any;
    loading: boolean;
    error: string | null;
  }>({
    auditTrail: null,
    loading: true,
    error: null
  });

  // Order editing state
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [modal, setModal] = useState<{
    open: boolean;
    variant: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    details?: string[];
  }>({ open: false, variant: 'info', message: '' });

  // Direct API call without any caching
  const loadOrdersDirect = useCallback(async () => {
    setDirectLoading(true);
    setDirectError(null);
    try {
      const result = await simulatorsApi.getSystemOrders(systemType, { limit: 100 });
      setDirectOrders(result);
    } catch (error) {
      setDirectError(error instanceof Error ? error.message : 'Failed to load orders');
    } finally {
      setDirectLoading(false);
    }
  }, [systemType]);

  // Load data immediately when component mounts or systemType changes
  useEffect(() => {
    loadOrdersDirect();
  }, [systemType, loadOrdersDirect]);

  // Use direct data for rendering (bypassing the hook)
  const systemOrders = directOrders;
  const ordersLoading = directLoading;
  const ordersError = directError;
  const refreshOrders = loadOrdersDirect;

  // Create realistic order for current system
  const createOrderForSystem = async () => {
    setDirectLoading(true);
    try {
      const timestamp = Date.now();
      const orderData = {
        order_id: `ORD-${timestamp}`, // ← Required field that was missing!
        customer_name: `Test Customer ${timestamp}`,
        customer_email: `test${timestamp}@example.com`,
        total_amount: Math.floor(Math.random() * 200) + 50, // Random amount $50-$250
        tax_amount: 0.0,
        status: 'active',
        items: [{
          sku: 'TEST-PRODUCT',
          qty: 1,
          price: Math.floor(Math.random() * 200) + 50
        }]
      };

      console.log(`Creating order in ${currentSystem.displayName}:`, orderData);

      const response = await fetch(`${API_BASE}/simulators/${systemType}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Created order in ${currentSystem.displayName}:`, result.order_id);
        
        // Refresh data after creating order
        await loadOrdersDirect();
        await loadAuditData();
      } else {
        const errorText = await response.text();
        console.error('Failed to create order:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error creating order:', error);
    } finally {
      setDirectLoading(false);
    }
  };

  // FALLBACK: Keep the hook approach as backup
  // const uniqueCacheKey = `system-orders-${systemType}-${Date.now()}`;
  // const { data: hookSystemOrders, loading: hookOrdersLoading, error: hookOrdersError, refresh: hookRefreshOrders } = useCachedManualCheck(
  //   async () => {
  //     console.log(`🔄 HOOK API Call: Loading orders for ${systemType}...`);
  //     try {
  //       const result = await simulatorsApi.getSystemOrders(systemType, { limit: 100 });
  //       console.log(`✅ HOOK API Response for ${systemType}:`, result);
  //       return result;
  //     } catch (error) {
  //       console.error(`❌ HOOK API Error for ${systemType}:`, error);
  //       throw error;
  //     }
  //   },
  //   uniqueCacheKey // Always unique cache key to bypass cache completely
  // );

  // Fallback audit trail hook
  const { data: auditTrail, loading: auditLoading, error: auditError, refresh: refreshAudit } = useCachedManualCheck(
    () => ordersApi.getAuditTrail(),
    'global-audit-trail'
  );

  // Direct audit trail loading (memoized to prevent re-registration loops)
  const loadAuditData = useCallback(async () => {
    try {
      setAuditData(prev => ({ ...prev, loading: true }));
      
      const result = await ordersApi.getAuditTrail();
      
      setAuditData({
        auditTrail: result,
        loading: false,
        error: null
      });
      
    } catch (error) {
      console.error('System audit data loading error:', error);
      setAuditData({
        auditTrail: null,
        loading: false,
        error: 'Failed to load audit trail'
      });
    }
  }, []); // No dependencies - always fetches fresh data

  // Memoized refresh handler to prevent re-registration loops
  const refreshHandler = useCallback(() => {
    loadOrdersDirect();
    loadAuditData();
  }, [loadOrdersDirect, loadAuditData]);

  // Register refresh handlers
  useRegisterRefresh(`system-${systemType}`, refreshHandler);

  // AUTO-LOAD audit trail when component mounts or systemType changes
  useEffect(() => {
    loadAuditData();
  }, [systemType, loadAuditData]);

  const refreshAll = () => {
    loadOrdersDirect(); // Use direct loading
    loadAuditData();
    // Also refresh hook as backup
    refreshAudit();
  };


  // Order editing functions
  const startEditOrder = (order: any) => {
    setEditingOrderId(order.order_id);
    setEditingOrder({ ...order }); // Create a copy for editing
  };

  const cancelEditOrder = () => {
    setEditingOrderId(null);
    setEditingOrder(null);
  };

  const saveEditOrder = async () => {
    if (!editingOrder) return;
    
    try {
      // Create update payload with only changed fields
      const updateData = {
        customer_name: editingOrder.customer_name,
        total_amount: editingOrder.total_amount,
        status: editingOrder.status
      };
      
      console.log('🔧 SAVE ORDER DEBUG:', {
        systemType,
        orderId: editingOrder.order_id,
        updateData,
        apiUrl: `/api/v1/simulators/${systemType}/orders/${editingOrder.order_id}`
      });
      
      // Save to backend database
      const updatedOrder = await simulatorsApi.updateOrder(systemType, editingOrder.order_id, updateData);
      
      // Update local state with server response
      setDirectOrders(prev => 
        prev ? prev.map(order => 
          order.order_id === editingOrder.order_id ? updatedOrder : order
        ) : prev
      );
      
      // Clear editing state
      setEditingOrderId(null);
      setEditingOrder(null);
      
      setModal({
        open: true,
        variant: 'success',
        title: `Order ${editingOrder.order_id} Updated`,
        message: 'AI Workflow is now running in the background.',
        details: [
          'Monitor Agent: Detecting order change',
          'Verify Agent: Cross-checking systems',
          'Execute Agent: Planning corrections',
          'Learn Agent: Recording patterns',
          'Check Agents page for workflow progress'
        ]
      });
      
      // Reload orders to see any changes and refresh audit trail automatically
      loadOrdersDirect();
      refreshAudit(); // ✅ AUTO-REFRESH audit trail so user sees changes immediately
      
    } catch (error: any) {
      console.error('🔧 SAVE ORDER ERROR:', {
        error,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
        errorStatus: error?.response?.status
      });
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      setModal({ open: true, variant: 'error', title: 'Save Failed', message: `Failed to save order: ${errorMsg}` });
    }
  };

  const updateEditingField = (field: string, value: any) => {
    if (!editingOrder) return;
    setEditingOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear audit trail function
  const clearAuditTrail = async () => {
    try {
      await ordersApi.clearAuditTrail();
      // No popup - just clear and refresh
      refreshAudit(); // Refresh to show empty state
    } catch (error) {
      setModal({ open: true, variant: 'error', title: 'Clear Failed', message: `Failed to clear audit trail: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  // Format audit action for clean readability
  const formatAuditAction = (entry: any) => {
    const before = entry.before_data || {};
    const after = entry.after_data || {};
    const changes: string[] = [];
    
    // Check common fields for changes
    if (before.total_amount !== after.total_amount) {
      changes.push(`Total Amount: $${before.total_amount} → $${after.total_amount}`);
    }
    if (before.tax_amount !== after.tax_amount) {
      changes.push(`Tax: $${before.tax_amount} → $${after.tax_amount}`);
    }
    if (before.customer_name !== after.customer_name) {
      changes.push(`Customer: ${before.customer_name} → ${after.customer_name}`);
    }
    if (before.status !== after.status) {
      changes.push(`Status: ${before.status} → ${after.status}`);
    }
    
    // If we found changes, show them
    if (changes.length > 0) {
      return `Order ${entry.entity_id}: ${changes.join(', ')}`;
    }
    
    // Fallback if no specific changes detected
    return `Order ${entry.entity_id} updated`;
  };

  // State tracking for debugging (disabled)
  // useEffect(() => {
  //   console.log(`🔍 SystemPage State Update for ${systemType}:`, {
  //     systemOrders,
  //     dataType: systemOrders ? typeof systemOrders : 'null',
  //     isArray: Array.isArray(systemOrders),
  //     ordersCount: Array.isArray(systemOrders) ? systemOrders.length : 'Not array',
  //     loading: ordersLoading,
  //     error: ordersError,
  //     firstOrderId: Array.isArray(systemOrders) && systemOrders[0] ? systemOrders[0].order_id : 'No orders'
  //   });
  // }, [systemType, systemOrders, ordersLoading, ordersError]);

  // Filter audit trail for this system - use direct data, fallback to hook data
  const finalAuditTrail = auditData.auditTrail || auditTrail;
  
  // Debug logging
  console.log('🔍 Audit Trail Debug:', {
    hasAuditData: !!auditData.auditTrail,
    hasHookData: !!auditTrail,
    finalAuditTrail: finalAuditTrail,
    hasData: !!finalAuditTrail?.data,
    hasAuditLogs: !!finalAuditTrail?.data?.audit_logs,
    auditLogsCount: finalAuditTrail?.data?.audit_logs?.length || 0,
    systemType: systemType
  });
  
  const systemAuditEntries = finalAuditTrail?.data?.audit_logs?.filter(
    (entry: any) => entry.system_type === systemType
  ) || [];
  
  console.log('🔍 Filtered Entries:', {
    totalLogs: finalAuditTrail?.data?.audit_logs?.length || 0,
    filteredCount: systemAuditEntries.length,
    systemType: systemType
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Package className="h-8 w-8 mr-3 text-blue-600" />
              {currentSystem.displayName} System
            </h1>
            <p className="text-gray-600 mt-2">{currentSystem.description}</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`px-4 py-2 rounded-lg border-2 ${currentSystem.color}`}>
              <span className="font-medium">System Active</span>
            </div>
            <button
              onClick={refreshAll}
              className="btn btn-outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh All
            </button>
          </div>
        </div>
      </div>

      {/* Orders Section - Full Width */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Package className="h-6 w-6 text-blue-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Orders</h2>
                  <p className="text-sm text-gray-600">Orders in {currentSystem.displayName}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={createOrderForSystem}
                  className="btn btn-sm btn-primary"
                  disabled={directLoading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Order
                </button>
                <button
                  onClick={refreshOrders}
                  className="btn btn-sm btn-outline"
                  disabled={ordersLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${ordersLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">

            {ordersLoading && !systemOrders ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner text={`Loading ${currentSystem.displayName} orders...`} />
              </div>
            ) : ordersError && !systemOrders ? (
              <ErrorMessage 
                message={`Failed to load ${currentSystem.displayName} orders`} 
                onRetry={refreshOrders} 
              />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    {Array.isArray(systemOrders) && systemOrders.length > 0 
                      ? `Showing All ${systemOrders.length} Orders` 
                      : `No Orders Found`}
                    {systemOrders && !Array.isArray(systemOrders) && (
                      <span className="text-red-600 text-sm ml-2">⚠️ Data not array: {typeof systemOrders}</span>
                    )}
                  </h3>
                </div>
                
                {Array.isArray(systemOrders) && systemOrders.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {systemOrders.map((order: any, index: number) => {
                      const isEditing = editingOrderId === order.order_id;
                      const displayOrder = isEditing ? editingOrder : order;
                      
                      return (
                        <div key={order.order_id || index} className={`rounded-lg p-4 transition-colors ${
                          isEditing ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Order Display - Numbered format */}
                              <h4 className="font-medium text-gray-900 mb-2">
                                Order {index + 1}: {displayOrder.order_id || `Unknown ID`}
                              </h4>
                              
                              {/* Customer Name */}
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={displayOrder.customer_name || ''}
                                  onChange={(e) => updateEditingField('customer_name', e.target.value)}
                                  className="text-sm text-gray-600 bg-white border border-gray-300 rounded px-2 py-1 mb-2 w-full max-w-xs"
                                  placeholder="Customer Name"
                                />
                              ) : (
                                <p className="text-sm text-gray-600 mb-2">{displayOrder.customer_name || 'Unknown Customer'}</p>
                              )}
                              
                              {/* Order Details */}
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                {/* Amount */}
                                <div className="flex items-center">
                                  <span className="mr-1">Amount:</span>
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={displayOrder.total_amount || '0'}
                                      onChange={(e) => updateEditingField('total_amount', parseFloat(e.target.value))}
                                      className="bg-white border border-gray-300 rounded px-1 py-0.5 w-20"
                                    />
                                  ) : (
                                    <span>${displayOrder.total_amount || '0.00'}</span>
                                  )}
                                </div>
                                
                                {/* Items Count */}
                                <span>Items: {displayOrder.items?.length || 0}</span>
                                
                                {/* System */}
                                <span>System: {currentSystem.displayName}</span>
                              </div>
                            </div>
                            
                            {/* Status and Actions */}
                            <div className="flex items-center space-x-3 ml-4">
                              {/* Status */}
                              {isEditing ? (
                                <select
                                  value={displayOrder.status || 'pending'}
                                  onChange={(e) => updateEditingField('status', e.target.value)}
                                  className="px-2 py-1 rounded-full text-xs font-medium bg-white border border-gray-300"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="processing">Processing</option>
                                  <option value="completed">Completed</option>
                                  <option value="shipped">Shipped</option>
                                  <option value="inventory_issue">Inventory Issue</option>
                                  <option value="payment_confirmed">Payment Confirmed</option>
                                  <option value="payment_failed">Payment Failed</option>
                                  <option value="error">Error</option>
                                </select>
                              ) : (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  displayOrder.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  displayOrder.status === 'payment_confirmed' ? 'bg-green-100 text-green-800' :
                                  displayOrder.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                  displayOrder.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                  displayOrder.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  displayOrder.status === 'inventory_issue' ? 'bg-orange-100 text-orange-800' :
                                  displayOrder.status === 'payment_failed' ? 'bg-red-100 text-red-800' :
                                  displayOrder.status === 'error' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {displayOrder.status || 'Unknown'}
                                </span>
                              )}
                              
                              {/* Action Buttons */}
                              {isEditing ? (
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={saveEditOrder}
                                    className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                                    title="Save changes"
                                  >
                                    <Save className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={cancelEditOrder}
                                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                                    title="Cancel editing"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEditOrder(order)}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                  title="Edit order"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="text-gray-500 mt-2">No orders found in {currentSystem.displayName}</p>
                    <p className="text-sm text-gray-400">Orders will appear here when available</p>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>

      {/* Audit Trail Section - Full Width */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <History className="h-6 w-6 text-orange-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Audit Trail</h2>
                  <p className="text-sm text-gray-600">Changes in {currentSystem.displayName}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={loadAuditData}
                  className="btn btn-sm btn-outline"
                  disabled={auditData.loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${auditData.loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={clearAuditTrail}
                  className="btn btn-sm btn-outline text-red-600 border-red-300 hover:bg-red-50"
                  disabled={auditData.loading}
                >
                  <Trash className="h-4 w-4 mr-2" />
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {auditData.loading && !finalAuditTrail ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner text="Loading audit trail..." />
              </div>
            ) : auditData.error && !finalAuditTrail ? (
              <ErrorMessage 
                message="Failed to load audit trail" 
                onRetry={loadAuditData} 
              />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    System Changes: {systemAuditEntries.length}
                  </h3>
                </div>
                
                {systemAuditEntries.length ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {systemAuditEntries.map((entry: any, index: number) => (
                      <div key={entry.id || index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Compact Action & Changes */}
                            <div className="space-y-2">
                              <div className="font-medium text-gray-900">
                                {formatAuditAction(entry)}
                              </div>
                              
                              {entry.changes && (
                                <div className="text-xs">
                                  {(() => {
                                    const changes = extractAuditChanges(entry);
                                    return changes.length > 0 ? (
                                      <div className="bg-gray-100 p-2 rounded space-y-1">
                                        {changes.map((change, i) => (
                                          <div key={i} className="flex items-center space-x-1 text-xs">
                                            <span className="font-medium">{change.displayName}:</span>
                                            <span className="text-red-600">{change.format(change.before)}</span>
                                            <span className="text-gray-400">→</span>
                                            <span className="text-green-600 font-medium">{change.format(change.after)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 italic text-xs">No changes detected</span>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                            
                            {/* Actor & System Info */}
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>By: {entry.actor_name || 'System'}</span>
                              <span>System: {currentSystem.displayName}</span>
                              <span>{formatAuditTimestamp(entry.created_at)}</span>
                            </div>
                          </div>
                          
                          {/* Status Icon */}
                          <div className="ml-4 flex items-center space-x-2">
                            {entry.action === 'update_order' ? (
                              <Edit className="h-4 w-4 text-blue-500" />
                            ) : entry.action === 'create_order' ? (
                              <Plus className="h-4 w-4 text-green-500" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                            )}
                            {entry.requires_monitoring && (
                              <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                                Monitor
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="text-gray-500 mt-2">No audit entries for {currentSystem.displayName}</p>
                    <p className="text-sm text-gray-400">Changes will appear here when they occur</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      <Modal
        open={modal.open}
        onClose={() => setModal(m => ({ ...m, open: false }))}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        details={modal.details}
      />
    </div>
  );
};

export default SystemPage;
